import React, { useEffect, useRef, useState } from "react";
import { Accessibility, X } from "lucide-react";

/**
 * Floating accessibility toolbar — top-right, fixed.
 *
 * Toggles a small set of WCAG-friendly preferences, all persisted to
 * localStorage and applied as CSS classes on <html>. Designed to be
 * non-intrusive: the trigger is a cog icon, the panel is keyboard-navigable,
 * and `aria-pressed` reflects each toggle's state for screen-readers.
 */

const STORAGE_KEY = "motowear_a11y_v1";

const DEFAULT_PREFS = {
  darkMode: false,
  calmMode: false,
  readingSupport: false,
  largerText: false,
  highContrast: false,
  reducedMotion: false,
  focusOutlines: false,
  underlineLinks: false,
};

const TOGGLES = [
  { key: "darkMode", label: "Dark mode", hint: "Low-stimulation dark palette — easier on tired eyes." },
  { key: "calmMode", label: "Calm view", hint: "Hides promo banners and stops moving graphics." },
  { key: "readingSupport", label: "Reading support", hint: "OpenDyslexic font + extra line-spacing for easier reading." },
  { key: "largerText", label: "Larger text", hint: "Bumps body copy +20%." },
  { key: "highContrast", label: "High contrast", hint: "Pure black on white, stronger borders." },
  { key: "reducedMotion", label: "Reduce motion", hint: "Disables hero rotators and transition effects." },
  { key: "focusOutlines", label: "Always-on focus rings", hint: "Strong outline on every focused element." },
  { key: "underlineLinks", label: "Underline all links", hint: "Adds an underline to every clickable link." },
];

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function applyPrefs(prefs) {
  const root = document.documentElement;
  Object.entries(prefs).forEach(([k, v]) => {
    const cls = `a11y-${k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`;
    if (v) root.classList.add(cls);
    else root.classList.remove(cls);
  });
  // Let opt-in components (e.g. GlossaryText for Reading Support) re-render
  // without polling the DOM. Fires after every toggle.
  try { window.dispatchEvent(new CustomEvent("motowear:a11y-changed", { detail: prefs })); } catch { /* SSR-safe */ }
}

export default function AccessibilityToolbar() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Apply on mount + any pref change.
  useEffect(() => {
    applyPrefs(prefs);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  }, [prefs]);

  // Close on Escape, click-outside, and focus trap basics.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !triggerRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));
  const reset = () => setPrefs({ ...DEFAULT_PREFS });

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Open accessibility options"
        title="Accessibility options"
        className="fixed bottom-4 left-4 lg:top-1/2 lg:left-auto lg:right-3 lg:bottom-auto lg:-translate-y-1/2 z-[80] bg-white border border-black/15 shadow-lg w-12 h-12 lg:w-11 lg:h-11 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
        data-testid="a11y-trigger"
      >
        <Accessibility size={22} strokeWidth={1.75} aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby="a11y-title"
          className="fixed bottom-20 left-4 lg:bottom-6 lg:left-auto lg:right-16 lg:top-auto lg:translate-y-0 z-[80] w-[320px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-7rem)] bg-white border border-black/20 shadow-2xl flex flex-col"
          data-testid="a11y-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 flex-shrink-0">
            <h2 id="a11y-title" className="font-display text-lg tracking-tight">Accessibility</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close accessibility options"
              className="p-1 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-black"
              data-testid="a11y-close"
            >
              <X size={18} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
          <ul className="p-2 overflow-y-auto flex-1" data-testid="a11y-panel-list">
            {TOGGLES.map((t) => {
              const on = !!prefs[t.key];
              return (
                <li key={t.key}>
                  <button
                    type="button"
                    onClick={() => toggle(t.key)}
                    aria-pressed={on}
                    className={`w-full text-left p-3 flex items-start gap-3 border ${on ? "border-black bg-black text-white" : "border-transparent hover:bg-black/5"} focus:outline-none focus:ring-2 focus:ring-black`}
                    data-testid={`a11y-toggle-${t.key}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`mt-1 w-9 h-5 rounded-full relative transition-colors ${on ? "bg-white" : "bg-black/15"}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${on ? "left-[18px] bg-black" : "left-0.5 bg-white"}`} />
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm font-medium">{t.label}</span>
                      <span className={`block text-xs mt-0.5 ${on ? "text-white/70" : "text-black/55"}`}>{t.hint}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-3 border-t border-black/10 flex items-center justify-between flex-shrink-0">
            <button
              type="button"
              onClick={reset}
              className="label-eyebrow text-xs underline focus:outline-none focus:ring-2 focus:ring-black"
              data-testid="a11y-reset"
            >
              Reset to default
            </button>
            <a
              href="/terms#17-accessibility"
              className="label-eyebrow text-xs text-black/60 hover:text-black focus:outline-none focus:ring-2 focus:ring-black"
              data-testid="a11y-policy-link"
            >
              Read policy
            </a>
          </div>
        </div>
      )}
    </>
  );
}
